/**
 * WHAT I TOLD THEM, AND WHAT THEY SAID BACK.
 *
 * WHY THIS SCREEN EARNS ITS PLACE. A reporter is emailed when their fault is
 * fixed or closed, and that is the important half — but email is a place things
 * get lost, filtered and deleted, and somebody who wants to know where a report
 * got to should not have to go looking through their inbox for a message we
 * might not have sent yet. This is the place to look.
 *
 * IT IS THE ONLY THING IN THE APP THAT READS ACROSS A WORKSPACE BOUNDARY, so
 * everything on it comes from one deliberately narrow answer — see
 * supabase/functions/my-reports. It is not an issue and cannot become one: no
 * description, no board, no assignee, and no way to reach anybody else's.
 *
 * ONE SCREEN, NOT A THREAD PER REPORT. Somebody with three reports open wants
 * to see the three of them, and a list that makes them click into each one to
 * find out whether anything happened is a list that gets checked once.
 *
 * A PANEL, NOT A DIALOG. It started as a second thing in the sidebar next to
 * the report button, which was two doors to one room: sending a report and
 * finding out what happened to it are the same errand, and a product people pay
 * for does not make them look in two places for it. It is now a tab inside
 * Feedback, so this renders content and nothing around it.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../store';
import { asDate } from '../utils';
import type { ReportedByMe } from '../backend/types';

/** The same clock the rest of the app uses for something a person wrote. */
function when(at: string): string {
  const then = new Date(at).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days < 30 ? `${days}d ago` : asDate(then);
}

/**
 * The dot beside where it stands. Colour AND the word, never colour alone —
 * the words come from the server already translated out of our column names.
 */
const TONE: Record<string, string> = {
  'Not started yet': 'backlog',
  'Being worked on': 'inprogress',
  Fixed: 'done',
  Closed: 'wontdo',
};

export default function MyReports() {
  const myReports = useApp((s) => s.myReports);
  const [reports, setReports] = useState<ReportedByMe[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let stillHere = true;
    myReports()
      .then((list) => stillHere && setReports(list))
      .catch(() => stillHere && setFailed(true));
    return () => {
      stillHere = false;
    };
  }, [myReports]);

  return (
    <>
        {reports === null && !failed && <p className="hint">Looking…</p>}

        {failed && (
          <p className="hint" role="alert">
            Your reports could not be loaded. They are safe — try again in a moment.
          </p>
        )}

        {/*
          NOTHING YET IS NOT AN ERROR, and it is the first thing most people
          will see here. It says what this screen is FOR rather than apologising
          for being empty.
        */}
        {reports?.length === 0 && (
          <p className="hint">
            You have not sent us anything yet. When you do, this is where you can see what
            happened to it.
          </p>
        )}

        {/*
          WHY THERE ARE REPORTS HERE FROM SOMEWHERE ELSE. This list is keyed on
          the PERSON, not the workspace they happen to have open — a fault they
          reported does not stop being theirs because they changed hats, and a
          list that emptied itself on switching would read as reports being lost.
          Said out loud because it surprised the first person to try it.
        */}
        {reports && reports.length > 0 && (
          <p className="hint">
            Everything you have reported, from any workspace. Only you can see this.
          </p>
        )}

        {reports && reports.length > 0 && (
          <div className="reports">
            {reports.map((r) => (
              <div key={r.ref} className="report">
                <div className="report-top">
                  <span className={`report-stands is-${TONE[r.stands] ?? 'backlog'}`}>
                    {r.stands}
                  </span>
                  <span className="report-ref">{r.ref}</span>
                  {r.from && <span className="report-from">from {r.from}</span>}
                  <span className="report-when">{when(r.at)}</span>
                </div>
                <p className="report-title">{r.title}</p>

                {/*
                  WHAT WE SAID, IN FULL. Truncating an answer to somebody's
                  problem to keep a list tidy is the wrong trade — the answer is
                  the entire reason they opened this.
                */}
                {r.replies.length > 0 ? (
                  r.replies.map((c) => (
                    <div key={c.at} className="report-reply">
                      <span className="report-reply-who">FlightDeck · {when(c.at)}</span>
                      <p>{c.text}</p>
                    </div>
                  ))
                ) : (
                  /*
                   * SAID PLAINLY rather than left blank. A card with nothing
                   * under it reads as a screen that failed to load, and the
                   * honest fact — we have not answered yet — is more use than
                   * the silence it looks like.
                   */
                  <p className="hint report-quiet">No reply yet.</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Said here as well as in the mail, because somebody who has come
            looking is exactly the person wondering whether they have to. */}
        <p className="hint">
          We email you as well, when something you reported is fixed or closed.
        </p>
    </>
  );
}
